import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './ContainerLinkExtractor.css';

interface Match {
  address: string;
  folderUrl: string | null;
  folderName: string | null;
  similarity: number;
}

interface UploadResponse {
  success: boolean;
  message: string;
  requiredColumns?: string[];
  folders?: Array<{ id: string; name: string }>;
  folderCount?: number;
  matches?: Match[];
  totalAddresses?: number;
  matchedCount?: number;
  downloadUrl?: string;
}

interface ErrorResponse {
  error: string;
  code?: string;
  details?: unknown;
}

function ContainerLinkExtractor() {
  const [file, setFile] = useState<File | null>(null);
  const [boxFolderUrl, setBoxFolderUrl] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string; folders?: Array<{ id: string; name: string }>; matches?: Match[]; totalAddresses?: number; matchedCount?: number; downloadUrl?: string } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setResult({ type: 'error', message: 'Please select a file first.' });
      return;
    }

    if (!boxFolderUrl || boxFolderUrl.trim() === '') {
      setResult({ type: 'error', message: 'Please enter a Box folder URL.' });
      return;
    }

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('boxFolderUrl', boxFolderUrl.trim());

    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data: UploadResponse | ErrorResponse = await response.json();

      if (response.ok && 'success' in data && data.success) {
        setResult({ 
          type: 'success', 
          message: data.message,
          folders: data.folders || [],
          matches: data.matches || [],
          totalAddresses: data.totalAddresses || 0,
          matchedCount: data.matchedCount || 0,
          downloadUrl: data.downloadUrl,
        });
      } else {
        const errorMessage = 'error' in data ? data.error : 'Upload failed';
        setResult({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      setResult({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to upload file. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ContainerLinkExtractor">
      <header className="ContainerLinkExtractor-header">
        <Link to="/" className="back-link">
          <img 
            src="https://app.soteriamarket.com/banner3.jpeg" 
            alt="Soteria Logo" 
            className="logo"
          />
        </Link>
        <h1>Container Link Extractor</h1>
      </header>
      
      <main className="ContainerLinkExtractor-main">
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="file-input-wrapper">
            <label htmlFor="file-upload" className="file-label">
              Choose Excel File
            </label>
            <p className="explainer-text">
              Upload an Excel file with required columns: <strong>Address</strong> and <strong>Data Room Link</strong>
            </p>
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              disabled={uploading}
              className="file-input"
            />
            {file && (
              <div className="file-info">
                <span className="file-name">{file.name}</span>
                <span className="file-size">({(file.size / 1024).toFixed(2)} KB)</span>
              </div>
            )}
          </div>

          <div className="url-input-wrapper">
            <label htmlFor="box-folder-url" className="file-label">
              Box Folder URL
            </label>
            <input
              id="box-folder-url"
              type="url"
              value={boxFolderUrl}
              onChange={(e) => setBoxFolderUrl(e.target.value)}
              placeholder="https://yourdomain.box.com/folder/123456789"
              disabled={uploading}
              className="url-input"
            />
          </div>

          <button 
            type="submit" 
            disabled={!file || !boxFolderUrl.trim() || uploading}
            className="upload-button"
          >
            {uploading ? 'Processing...' : 'Upload & Validate'}
          </button>
        </form>

        {uploading && (
          <div className="progress-indicator">
            <div className="spinner"></div>
            <p>Processing your request...</p>
            <p className="progress-text">Validating Excel file and accessing Box folder</p>
          </div>
        )}

        {result && (
          <div className={`result ${result.type}`}>
            <div className="result-icon">
              {result.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="result-content">
              <div className="result-message">{result.message}</div>
              {result.type === 'success' && result.downloadUrl && (
                <div className="download-section">
                  <a 
                    href={`${API_BASE_URL}${result.downloadUrl}`}
                    download
                    className="download-button"
                  >
                    📥 Download Updated Excel File
                  </a>
                  <p className="download-note">
                    The file has been updated with folder links for addresses with match scores above 50%.
                  </p>
                </div>
              )}
              {result.type === 'success' && result.matches && result.matches.length > 0 && (
                <div className="matches-section">
                  <h3>Address Matches ({result.matchedCount || 0} of {result.totalAddresses || 0} matched)</h3>
                  <div className="matches-table-wrapper">
                    <table className="matches-table">
                      <thead>
                        <tr>
                          <th>Address</th>
                          <th>Matched Folder</th>
                          <th>Folder URL</th>
                          <th>Match Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.matches.map((match, index) => (
                          <tr key={index} className={match.folderUrl ? 'matched' : 'unmatched'}>
                            <td>{match.address}</td>
                            <td>{match.folderName || '—'}</td>
                            <td>
                              {match.folderUrl ? (
                                <a href={match.folderUrl} target="_blank" rel="noopener noreferrer" className="folder-link">
                                  {match.folderUrl}
                                </a>
                              ) : (
                                <span className="no-match">No match found</span>
                              )}
                            </td>
                            <td>
                              {match.similarity > 0 ? (
                                <span className="similarity-score">{(match.similarity * 100).toFixed(1)}%</span>
                              ) : (
                                <span className="no-match">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ContainerLinkExtractor;
